import React, { useState, useEffect } from 'react';
import { Menu, X, Code2, Database, Server, Boxes, Mail, Phone, MapPin, Github, Linkedin } from 'lucide-react';

const NitidusWebsite: React.FC = () => {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (section: string) => {
    setActiveSection(section);
    setIsMenuOpen(false);
    const element = document.getElementById(section);
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      {/* Header */}
      <header className={`fixed w-full top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-gray-900/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'}`}>
        <nav className="container mx-auto px-6 py-4 max-w-4xl">
          {/*<div className="flex items-center justify-between">*/}
          <div className="flex items-center w-full">
            {/*<div className="flex items-center space-x-2">}*/}
            <div className="flex items-center space-x-2 ml-0">
              <a href="https://www.nitidus.tec.br" target="_self" rel="noopener noreferrer">
                <img src="/logo_nitidus_tec64.png" alt="Nitidus.tec Logo" className="w-32 h-36" />
              </a>
            </div>

            {/* Desktop Menu */}
            {/*<div className="hidden md:flex space-x-8 text-xl">*/}
            <div className="hidden md:flex space-x-8 text-xl ml-auto">
              {['home', 'desenvolvimento', 'api', 'contato'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className={`capitalize transition-colors hover:text-blue-400 ${
                    activeSection === item ? 'text-blue-400' : 'text-gray-300'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-gray-300"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-4 text-base">
              {['home', 'desenvolvimento', 'api', 'contato'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item)}
                  className="block w-full text-left capitalize py-2 hover:text-blue-400 transition-colors"
                >
                  {item}
                </button>
              ))}
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6 min-h-screen flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-gray-900"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent animate-fade-in">
              Desenvolvimento Full Stack Moderno
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Especialistas em React, TypeScript, Django e PostgreSQL. 
              Criamos soluções robustas e escaláveis para o seu negócio.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection('desenvolvimento')}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all transform hover:scale-105"
              >
                Conheça Nossas Tecnologias
              </button>
              <button
                onClick={() => scrollToSection('contato')}
                className="px-8 py-4 border-2 border-blue-400 rounded-lg font-semibold hover:bg-blue-400/10 transition-all"
              >
                Entre em Contato
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Desenvolvimento Section */}
      {/*<section id="desenvolvimento" className="py-20 px-6 bg-gray-800/50"> */}
      <section id="desenvolvimento" className="py-20 px-6 bg-gray-800/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Tecnologias & Desenvolvimento
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: <Code2 className="w-12 h-12" />,
                title: 'React JS + TypeScript',
                description: 'Interfaces modernas e responsivas com tipagem estática para maior segurança e produtividade no desenvolvimento.',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: <Server className="w-12 h-12" />,
                title: 'Django & DRF',
                description: 'Backend robusto com Django e Django Rest Framework para APIs RESTful escaláveis e seguras.',
                gradient: 'from-green-500 to-emerald-500'
              },
              {
                icon: <Database className="w-12 h-12" />,
                title: 'PostgreSQL',
                description: 'Banco de dados relacional poderoso, confiável e com recursos avançados para aplicações complexas.',
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: <Boxes className="w-12 h-12" />,
                title: 'Arquitetura Moderna',
                description: 'Padrões de projeto, clean code e melhores práticas para código maintível e escalável.',
                gradient: 'from-orange-500 to-red-500'
              },
              {
                icon: <Code2 className="w-12 h-12" />,
                title: 'APIs RESTful',
                description: 'Desenvolvimento de APIs robustas, documentadas e seguindo padrões REST para integração perfeita.',
                gradient: 'from-indigo-500 to-blue-500'
              },
              {
                icon: <Database className="w-12 h-12" />,
                title: 'Performance',
                description: 'Otimização de queries, caching e estratégias para aplicações de alta performance.',
                gradient: 'from-yellow-500 to-orange-500'
              }
            ].map((tech, index) => (
              <div
                key={index}
                className="group bg-gray-800 rounded-xl p-8 hover:bg-gray-750 transition-all duration-300 hover:shadow-2xl hover:scale-105 border border-gray-700 hover:border-gray-600"
              >
                <div className={`bg-gradient-to-r ${tech.gradient} p-4 rounded-lg inline-block mb-4 group-hover:scale-110 transition-transform`}>
                  {tech.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-white">{tech.title}</h3>
                <p className="text-gray-400 leading-relaxed">{tech.description}</p>
              </div>
            ))}
          </div>

          <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-2xl p-8 md:p-12 border border-blue-500/30">
            <h3 className="text-3xl font-bold mb-6 text-white">Stack Completa</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-xl font-semibold mb-4 text-blue-400">Frontend</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• React JS com Hooks modernos</li>
                  <li>• TypeScript para tipagem segura</li>
                  <li>• Tailwind CSS para estilização</li>
                  <li>• Estado global com Context/Redux</li>
                  <li>• Componentização e reutilização</li>
                </ul>
              </div>
              <div>
                <h4 className="text-xl font-semibold mb-4 text-purple-400">Backend</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• Django para aplicações robustas</li>
                  <li>• Django Rest Framework (DRF)</li>
                  <li>• PostgreSQL otimizado</li>
                  <li>• Autenticação JWT/OAuth</li>
                  <li>• Testes automatizados</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* API Section */}
      <section id="api" className="py-20 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
            APIs Disponíveis
          </h2>
          
          <div className="bg-gray-800 rounded-2xl p-8 md:p-12 border border-gray-700 text-center">
            <Server className="w-20 h-20 mx-auto mb-6 text-blue-400 animate-pulse" />
            <h3 className="text-2xl font-bold mb-4">Em Breve</h3>
            <p className="text-gray-400 text-lg mb-6 leading-relaxed">
              Estamos desenvolvendo APIs públicas para facilitar a integração de suas aplicações. 
              Em breve disponibilizaremos documentação completa e endpoints para diversos casos de uso.
            </p>
            <div className="inline-block bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-lg px-6 py-3">
              <p className="text-blue-300 font-semibold">APIs em desenvolvimento</p>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {['RESTful APIs', 'Documentação OpenAPI', 'Autenticação Segura'].map((feature, index) => (
              <div key={index} className="bg-gray-800/50 rounded-lg p-6 text-center border border-gray-700">
                <p className="text-gray-300 font-medium">{feature}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contato Section */}
      <section id="contato" className="py-20 px-6 bg-gray-800/50">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-4xl md:text-5xl font-bold mb-12 text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Entre em Contato
          </h2>
          
          <div className="bg-gray-800 rounded-2xl p-8 md:p-12 border border-gray-700">
            <p className="text-gray-300 text-lg mb-8 text-center leading-relaxed">
              Tem um projeto em mente? Vamos conversar sobre como podemos ajudar a transformar suas ideias em realidade.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="flex flex-col items-center text-center p-6 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors">
                <Mail className="w-10 h-10 text-blue-400 mb-3" />
                <h4 className="font-semibold mb-2">Email</h4>
                <p className="text-gray-400 text-sm">contato@nitidus.tec.br</p>
              </div>
              
              <div className="flex flex-col items-center text-center p-6 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors">
                <MapPin className="w-10 h-10 text-purple-400 mb-3" />
                <h4 className="font-semibold mb-2">Localização</h4>
                <p className="text-gray-400 text-sm">Maringá, Paraná - BR</p>
              </div>
              
              <div className="flex flex-col items-center text-center p-6 bg-gray-750 rounded-lg hover:bg-gray-700 transition-colors">
                <Phone className="w-10 h-10 text-green-400 mb-3" />
                <h4 className="font-semibold mb-2">Redes Sociais</h4>
                <div className="flex gap-3 mt-2">
                  <Github className="w-6 h-6 text-gray-400 hover:text-white cursor-pointer transition-colors" />
                  <Linkedin className="w-6 h-6 text-gray-400 hover:text-white cursor-pointer transition-colors" />
                </div>
              </div>
            </div>

            <div className="text-center">
              <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg font-semibold hover:shadow-lg hover:shadow-purple-500/50 transition-all transform hover:scale-105">
                Iniciar Conversa
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 py-8 px-6 border-t border-gray-800">
        <div className="container mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Code2 className="w-6 h-6 text-blue-400" />
            <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
              Nitidus.tec.br
            </span>
          </div>
          <p className="text-gray-500">
            © 2025 Nitidus Tecnologia. Desenvolvimento Full Stack Moderno.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default NitidusWebsite;